import React, {useEffect, useMemo, useState} from 'react';
import {Link, Navigate, Route, Routes, useLocation, useNavigate} from 'react-router-dom';

import {useAuth0} from '@auth0/auth0-react';
import {ErrorBoundary} from '@sentry/react';
import logoSrc from 'src/assets/PitchBookLogo.svg';
import {DefaultAppFooter} from 'src/components/default-footer.component';
import {SplashScreen} from 'src/components/splash-screen/SplashScreen';
import {ACCESS_MANAGEMENT, PB_ADMIN, VENDOR_ADMIN} from 'src/hooks/access-resource-constant';
import {useCurrentUser} from 'src/hooks/useCurrentUser';

import Group, {Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import Loading from '@pb/kit-component-loading';
import {PageHeader, PageLogo, PageMain} from '@pb/kit-component-page-v2';
import {StickyElement} from '@pb/kit-component-sticky-element';
import Tabs, {Tab} from '@pb/kit-component-tabs-v2';

import {AccessManagement} from '../access-management/access-management';
import {AuthCallback} from '../authentication';
import {UnauthorizedPage} from '../authentication/unauthorized-page';
import {Dashboard} from '../dashboard/dashboard';
import {useSentry} from '../sentry';
import {SsoSettings} from '../sso-settings/sso-settings';
import {UserProfile} from '../user-profile/user-profile';
import {VendorAdminLendingPage} from '../vendor-admin/vendor-admin-lending-page';
import {VendorManagement} from '../vendor-management/vendor-management';
import {VendorProfile} from '../vendor-profile/vendor-profile';

import {UserCorner} from './components/user-corner';

import {logoIcon, main, mainContent, mainHeader, mainStickyContainer} from './main.module.styl';

export const Main = () => {
    const {isAuthenticated, isLoading} = useAuth0();
    const navigate = useNavigate();
    const location = useLocation();
    const {hasAccessToResource, isDataLoaded, isUnauthorized} = useCurrentUser();
    const [minDelayDone, setMinDelayDone] = useState(false);
    const canViewUsers = useMemo(() => hasAccessToResource(PB_ADMIN.USER_READ), [hasAccessToResource]);
    const canViewRequests = useMemo(() => hasAccessToResource(VENDOR_ADMIN.READ_REQUESTS), [hasAccessToResource]);
    const canViewVendors = useMemo(() => hasAccessToResource(PB_ADMIN.VENDOR_READ), [hasAccessToResource]);
    const canViewAccessManagement = useMemo(
        () => hasAccessToResource(ACCESS_MANAGEMENT.TOOL_ACCESS), [hasAccessToResource],
    );

    useSentry();

    useEffect(() => {
        if (!isAuthenticated) {
            setMinDelayDone(false);
            return;
        }

        const timer = window.setTimeout(() => setMinDelayDone(true), 1000);
        return () => window.clearTimeout(timer);
    }, [isAuthenticated]);

    if (location.pathname === '/auth/callback') {
        return <AuthCallback/>;
    }

    const isReady = minDelayDone && isDataLoaded;
    const isUserDetailRoute = /^\/users\/[^/]+$/.test(location.pathname);
    const isVendorDetailRoute = /^\/vendors\/[^/]+$/.test(location.pathname);
    const isSplashReady = isReady || isUserDetailRoute || isVendorDetailRoute;

    if (isUnauthorized && isReady) {
        return <UnauthorizedPage/>;
    }

    const getActiveVendorTab = () => {
        if (location.pathname.startsWith('/vendor-admin')) {
            return canViewUsers || canViewVendors ? 'vendor-admin' : '';
        }
        if (location.pathname.startsWith('/vendors')) {
            return 'vendors';
        }
        return '';
    };

    const activeVendorTab = getActiveVendorTab();
    const isAccessManagementArea = location.pathname.startsWith('/access-management') || location.pathname.startsWith('/users/');

    return !isLoading ? (
        <ErrorBoundary>
            <>
                <SplashScreen isReady={isSplashReady} logoSrc={logoSrc}/>
                <div className={main}>
                    <PageMain className={mainContent}>
                        <StickyElement className={mainStickyContainer}>
                            <PageHeader>
                                <Item grow={1}>
                                    <Group direction="h" justify="space-between" className={mainHeader} alignItems="center">
                                        <Link to="/"><PageLogo><Icon type={Icons.PITCHBOOK_LOGO_V2} className={logoIcon}/></PageLogo></Link>
                                        <Group>
                                            {isAccessManagementArea && canViewAccessManagement ? (
                                                <Tabs tabsId="tabs-access-mgmt" tabPanelId="panel-access-mgmt" value="">
                                                    <Tab value="access-management" caption="Access Management" onClick={() => navigate('/access-management')}/>
                                                </Tabs>
                                            ) : null}
                                            {activeVendorTab ? (
                                                <Tabs tabsId="tabs-user-mgmt" tabPanelId="panel-user-mgmt" value={activeVendorTab}>
                                                    {canViewVendors ? <Tab value="vendors" caption="Vendor Management" onClick={() => navigate('/vendors')}/> : null }
                                                    {canViewRequests ? <Tab value="vendor-admin" caption="Third-party Vendor Admin" onClick={() => navigate('/vendor-admin/pending')}/> : null }
                                                </Tabs>
                                            ) : null}
                                            <UserCorner/>
                                        </Group>
                                    </Group>
                                </Item>
                            </PageHeader>
                        </StickyElement>
                        <Routes>
                            <Route path="/auth/callback" element={<AuthCallback/>}/>
                            <Route path="/" element={<Dashboard/>}/>
                            <Route path="/sso-settings/*" element={<SsoSettings/>}/>
                            <Route path="/access-management" element={<AccessManagement/>}/>
                            <Route path="/vendors/:vendorId" element={<VendorProfile/>}/>
                            <Route path="/vendors" element={<VendorManagement/>}/>
                            <Route path="/users/:userId" element={<UserProfile/>}/>
                            <Route path="/vendor-admin/*" element={<VendorAdminLendingPage/>}/>
                            <Route path="*" element={<Navigate to="/" replace={true}/>}/>
                        </Routes>
                    </PageMain>
                    {location.pathname !== '/' && <DefaultAppFooter/>}
                </div>
            </>
        </ErrorBoundary>
    ) : (
        <Loading fast={true}/>
    );
};
